import {
  type Channel,
  type PricingCalculateRequest,
  type PricingCalculateResponse,
  Quote,
  type Settings,
  addDaysIso,
  formatBRL,
  formatBps,
  formatIsoDate,
  isoDateInSaoPaulo,
} from '@deizi/shared';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { Field } from '../../components/Form';
import { Modal, Sheet } from '../../components/Overlays';
import { api, newIdempotencyKey } from '../../lib/api';
import { errorMessage } from '../../lib/messages';
import { parseBrDate } from '../../lib/numbers';
import { useInvalidateQuotes } from '../../lib/quotes-api';
import { useIsPhone } from '../../lib/use-media';
import { CustomerSearch, type CustomerChoice, emptyChoice } from '../customers/CustomerSearch';
import { Combobox } from '../../components/Combobox';
import { channelOption } from '../../components/pickers';

/**
 * "Salvar como orçamento" (boards QQ-Desktop, QQ-Celular-Resultado) with the "abaixo do mínimo"
 * reason step (board QQ-Alertas). The API recalculates and freezes the numbers.
 */
export function SaveQuoteDialog({
  request,
  result,
  channels,
  settings,
  description: initialDescription,
  defaultChannelId,
  overrideFor,
  slicedFileId,
  thumbnailFileId,
  variantId,
  onClose,
  onSaved,
}: {
  request: PricingCalculateRequest;
  result: PricingCalculateResponse;
  channels: Channel[];
  settings: Settings | undefined;
  description: string;
  defaultChannelId: string | null;
  /** "Seu preço" typed for a channel (centavos), if any. */
  overrideFor: (channelId: string) => number | null;
  slicedFileId: string | null;
  thumbnailFileId: string | null;
  /** "Começar de um produto": the catalog variant the item is. */
  variantId: string | null;
  onClose: () => void;
  onSaved: (quote: Quote) => void;
}) {
  const phone = useIsPhone();
  const invalidate = useInvalidateQuotes();
  const validityDays = settings?.rules.quoteValidityDays ?? 7;
  const [key] = useState(newIdempotencyKey);
  const [description, setDescription] = useState(initialDescription);
  const [customer, setCustomer] = useState<CustomerChoice>(emptyChoice);
  const [channelId, setChannelId] = useState(defaultChannelId ?? channels[0]?.id ?? '');
  const [validity, setValidity] = useState(
    formatIsoDate(addDaysIso(isoDateInSaoPaulo(), validityDays)),
  );
  const [notes, setNotes] = useState('');
  const [step, setStep] = useState<'form' | 'reason'>('form');
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const ch = result.channels.find((c) => c.channelId === channelId);
  const ev = ch?.evaluation;
  const quantity = result.totals.quantity;
  const priceLine = !ev
    ? 'Margem impossível neste canal: digite seu preço na tabela.'
    : `${formatBRL(ev.priceCentavos)}${quantity > 1 ? ` × ${quantity} = ${formatBRL(ev.priceCentavos * quantity)}` : ''}`;

  const save = useMutation({
    mutationFn: () => {
      const validUntil = parseBrDate(validity);
      return api.post(
        '/quotes',
        Quote,
        {
          customerId: customer.isNew ? null : customer.id,
          ...(customer.isNew
            ? {
                newCustomer: {
                  name: customer.newName.trim(),
                  whatsapp: customer.newWhatsapp.trim() || null,
                  marketplaceNickname: customer.newNickname.trim() || null,
                  preferredChannelId: channelId,
                },
              }
            : {}),
          channelId,
          ...(validUntil ? { validUntil } : {}),
          notes: notes.trim() || null,
          item: {
            description: description.trim(),
            request,
            unitPriceCentavos: overrideFor(channelId),
            belowMinimumReason: reason.trim() || null,
            slicedFileId,
            thumbnailFileId,
            variantId,
          },
        },
        { idempotencyKey: key },
      );
    },
    onSuccess: (quote) => {
      invalidate();
      onSaved(quote);
    },
  });

  const submit = () => {
    const e: Record<string, string> = {};
    if (!description.trim()) e.description = 'Descreva a peça.';
    if (customer.isNew ? !customer.newName.trim() : !customer.id)
      e.customer = 'Escolha ou crie a cliente antes de salvar.';
    else if (customer.isNew && !customer.newWhatsapp.trim() && !customer.newNickname.trim())
      e.customer = 'Informe o WhatsApp (ou o apelido no marketplace) para mandar o orçamento.';
    const validUntil = parseBrDate(validity);
    if (!validUntil) e.validity = 'Use DD/MM/AAAA.';
    else if (validUntil < isoDateInSaoPaulo()) e.validity = 'A validade não pode estar no passado.';
    if (!ev) e.channel = 'Escolha um canal com preço ou digite seu preço.';
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    if (ev && ev.state !== 'ok' && step === 'form') {
      setStep('reason');
      return;
    }
    if (step === 'reason' && reason.trim().length < 3) {
      setErrors({ reason: 'Escreva o motivo para salvar abaixo do mínimo.' });
      return;
    }
    save.mutate();
  };

  const minimum = settings?.costs.minimumMarginBps;
  const channelName = channels.find((c) => c.id === channelId)?.name ?? '';
  const reasonStep = ev ? (
    <>
      <p className="sm muted">
        Com este preço, {channelName} fica{' '}
        {ev.state === 'below_cost'
          ? 'abaixo do custo'
          : `abaixo da margem mínima${minimum != null ? ` de ${formatBps(minimum)}` : ''}`}
        :
      </p>
      <div className="card flat">
        <div className="gt">
          <div
            className={ev.state === 'below_cost' ? 'gtr hl' : 'gtr wl'}
            style={{ gridTemplateColumns: 'minmax(0,1.4fr) 1fr 1fr 1fr' }}
          >
            <span style={{ fontWeight: 500 }}>{channelName}</span>
            <span className="num">{formatBRL(ev.priceCentavos)}</span>
            <span
              className="num"
              style={{
                color: ev.state === 'below_cost' ? 'var(--err)' : 'var(--warn)',
                fontWeight: 600,
              }}
            >
              {formatBRL(ev.netProfitCentavos)}
            </span>
            <span className="num">
              <span className={ev.state === 'below_cost' ? 'badge err' : 'badge warn'}>
                {ev.state === 'below_cost' ? 'Prejuízo' : formatBps(ev.marginBps)}
              </span>
            </span>
          </div>
        </div>
      </div>
      <Field
        id="why"
        label="Motivo (obrigatório)"
        error={errors.reason}
        help="Fica registrado no histórico do orçamento."
      >
        <textarea
          id="why"
          className={errors.reason ? 'input err' : 'input'}
          rows={3}
          placeholder="Ex.: cliente antiga, compra em volume, queima de estoque de cor…"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </Field>
    </>
  ) : null;

  const form = (
    <>
      <Field id="qdesc" label="Descrição" error={errors.description}>
        <input
          id="qdesc"
          className="input"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Field>
      <CustomerSearch value={customer} onChange={setCustomer} error={errors.customer} />
      <div style={{ display: 'grid', gridTemplateColumns: phone ? '1fr' : '1fr 1fr', gap: 12 }}>
        <Field
          id="qch"
          label="Canal"
          error={errors.channel}
          help={errors.channel ? undefined : priceLine}
        >
          <Combobox
            id="qch"
            value={channelId}
            options={channels.map(channelOption)}
            onChange={setChannelId}
          />
        </Field>
        <Field
          id="qval"
          label="Validade"
          error={errors.validity}
          help={`Padrão: ${validityDays} dias a partir de hoje`}
        >
          <input
            id="qval"
            className="input tab"
            inputMode="numeric"
            value={validity}
            onChange={(e) => setValidity(e.target.value)}
          />
        </Field>
      </div>
      <Field id="qobs" label="Observações">
        <textarea
          id="qobs"
          className="input"
          rows={3}
          placeholder="Cor, prazo combinado, retirada…"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </Field>
    </>
  );

  const error = save.error ? <p className="help err">{errorMessage(save.error)}</p> : null;
  const title = step === 'reason' ? 'Salvar com preço abaixo do mínimo?' : 'Salvar como orçamento';
  const back = step === 'reason' ? 'Voltar e ajustar' : 'Cancelar';
  const confirm = save.isPending
    ? 'Salvando…'
    : step === 'reason'
      ? 'Salvar mesmo assim'
      : 'Salvar orçamento';
  const onBack = () => (step === 'reason' ? setStep('form') : onClose());

  if (phone) {
    return (
      <Sheet label={title} onClose={onClose}>
        <h2>{title}</h2>
        <div className="col" style={{ gap: 12, overflowY: 'auto', maxHeight: '70vh' }}>
          {step === 'reason' ? reasonStep : form}
          {error}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <button type="button" className="btn" onClick={onBack}>
            {back}
          </button>
          <button type="button" className="btn p" onClick={submit} disabled={save.isPending}>
            {confirm}
          </button>
        </div>
      </Sheet>
    );
  }
  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onBack}>
            {back}
          </button>
          <button type="button" className="btn p" onClick={submit} disabled={save.isPending}>
            {confirm}
          </button>
        </>
      }
    >
      {step === 'reason' ? reasonStep : form}
      {error}
    </Modal>
  );
}
